
import { TrackDownload } from '@/lib/types';
import { v4 as uuidv4 } from 'uuid';
import { addDays, format, subDays } from 'date-fns';

// Mock track titles
const mockTracks = [
  "Echoes of Midnight",
  "Neon Pulse",
  "Bass Odyssey",
  "Synthwave Dreams",
  "Dark Matter",
  "Cyber Groove",
  "Rhythmic Horizon"
];

// Generate a single mock track
const generateMockTrack = (
  trackName: string, 
  downloadCount: number, 
  createdAt: Date
): TrackDownload => {
  return {
    id: uuidv4(),
    trackId: uuidv4(), // Added missing trackId property
    artistId: uuidv4(),
    artistName: "Demo Artist",
    fileName: `${trackName.toLowerCase().replace(/\s+/g, '-')}.mp3`,
    fileSize: Math.floor(Math.random() * 10000000) + 2000000, // Random size between 2MB and 12MB
    fileType: "audio/mpeg",
    fileUrl: "#",
    trackName,
    downloadCount,
    spotifyUrl: null,
    createdAt: createdAt.toISOString(),
    description: `A sample track demonstrating ${trackName}`,
  };
};

// Generate mock data with a random total number of downloads
export const generateMockData = (): TrackDownload[] => {
  // Generate a random number of downloads between 500 and 5000
  const totalDownloads = Math.floor(Math.random() * 4500) + 500;
  
  const mockData: TrackDownload[] = [];
  const today = new Date();
  const trackDownloadCounts: Record<string, number> = {};
  
  // Initialize download counts for each track
  mockTracks.forEach(track => {
    // Distribute total downloads randomly but ensure each track gets some
    const downloadShare = Math.floor((Math.random() * 0.3 + 0.05) * totalDownloads);
    trackDownloadCounts[track] = downloadShare;
  });
  
  // Adjust to ensure total adds up to requested amount
  let currentTotal = Object.values(trackDownloadCounts).reduce((a, b) => a + b, 0);
  const adjustment = totalDownloads / currentTotal;
  
  mockTracks.forEach(track => {
    trackDownloadCounts[track] = Math.floor(trackDownloadCounts[track] * adjustment);
  });
  
  // Ensure we hit exactly the total by adjusting the first track
  currentTotal = Object.values(trackDownloadCounts).reduce((a, b) => a + b, 0);
  if (currentTotal !== totalDownloads) {
    const diff = totalDownloads - currentTotal;
    trackDownloadCounts[mockTracks[0]] += diff;
  }
  
  // Create mock tracks with their total download counts (for summary stats)
  const mockTracks30Days: TrackDownload[] = [];
  
  mockTracks.forEach(track => {
    // Create track with random creation date in the last 30-90 days
    const randomDaysAgo = Math.floor(Math.random() * 60) + 30;
    const creationDate = subDays(today, randomDaysAgo);
    
    mockTracks30Days.push(
      generateMockTrack(track, trackDownloadCounts[track], creationDate)
    );
  });
  
  // Now distribute downloads over days to create daily data
  // This is for the chart visualization
  for (let i = 29; i >= 0; i--) {
    const date = subDays(today, i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    // More downloads on weekends
    const multiplier = isWeekend ? 1.5 : 1;
    
    // Randomly distribute downloads across tracks for this day
    mockTracks.forEach(trackName => {
      // Only create daily data if the track still has downloads to allocate
      if (trackDownloadCounts[trackName] > 0) {
        // Determine how many downloads to allocate today
        const maxDailyDownloads = Math.min(
          Math.floor((Math.random() * 20 + 5) * multiplier), 
          trackDownloadCounts[trackName]
        );
        
        if (maxDailyDownloads > 0) {
          trackDownloadCounts[trackName] -= maxDailyDownloads;
          
          // Find the track in our mock data
          const trackIndex = mockTracks30Days.findIndex(t => t.trackName === trackName);
          if (trackIndex !== -1) {
            // This is a bit unrealistic as we're setting the creation date to today,
            // but it helps with filtering in the UI
            const mockTrack = {...mockTracks30Days[trackIndex]};
            mockTrack.id = uuidv4(); // Give it a unique ID
            mockTrack.createdAt = date.toISOString();
            mockTrack.downloadCount = maxDailyDownloads;
            
            mockData.push(mockTrack);
          }
        }
      }
    });
  }
  
  return [...mockTracks30Days, ...mockData];
};
